/**
 * ASCII Art Generator - Glitch 故障效果
 *
 * Combined 混合模式：RGB色散 + 块位移双重效果
 */

import type { GlitchConfig } from './types'
import type { EffectDirection } from '../types'
import { getDirectionVector, clamp, randomInt, randomFloat } from './helpers'

// ============================================
// Canvas 应用函数
// ============================================

/**
 * 应用 Glitch 故障效果到 Canvas
 * @param canvas 目标画布
 * @param config Glitch 配置
 */
export function applyGlitch(
  canvas: HTMLCanvasElement,
  config: GlitchConfig
): void {
  const ctx = canvas.getContext('2d')!
  const { width, height } = canvas
  const { rgbShift, blockShift, blockCount, direction, time } = config

  // 获取原始图像数据
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  // ============================================
  // 第一部分：RGB 色散效果
  // ============================================
  if (rgbShift > 0) {
    applyRgbShift(data, width, height, rgbShift, time, direction)
  }

  // ============================================
  // 第二部分：块位移效果
  // ============================================
  if (blockShift > 0 && blockCount > 0) {
    applyBlockShift(ctx, canvas, width, height, blockShift, blockCount, time)
  }

  // 写回画布
  ctx.putImageData(imageData, 0, 0)
}

// ============================================
// RGB 色散实现
// ============================================

/**
 * 应用 RGB 通道分离效果
 */
function applyRgbShift(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  rgbShift: number,
  time: number,
  direction: EffectDirection
): void {
  const dirVector = getDirectionVector(direction)

  // 时间动态偏移
  const timeOffset = Math.sin(time * 3) * rgbShift * 0.5
  const redOffset = Math.round(Math.abs(dirVector.dx) * (rgbShift + timeOffset))
  const blueOffset = -Math.round(Math.abs(dirVector.dx) * (rgbShift + timeOffset))

  // 创建临时数组存储原始数据
  const originalData = new Uint8ClampedArray(data)

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4

      // 红通道右移
      const redX = clamp(x + redOffset, 0, width - 1)
      const redI = (y * width + redX) * 4
      data[i] = originalData[redI]

      // 蓝通道左移
      const blueX = clamp(x + blueOffset, 0, width - 1)
      const blueI = (y * width + blueX) * 4
      data[i + 2] = originalData[blueI + 2]
    }
  }
}

// ============================================
// 块位移实现
// ============================================

/**
 * 应用块位移效果
 */
function applyBlockShift(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  blockShift: number,
  blockCount: number,
  time: number
): void {
  // 在画布上绘制错位块
  for (let i = 0; i < blockCount; i++) {
    // 随机选择块位置和大小
    const blockY = randomInt(0, height - 20)
    const blockHeight = randomInt(5, 30)
    const blockWidth = randomInt(width * 0.3, width * 0.8)

    // 随机位移量（基于时间变化）
    const shiftAmount = randomFloat(-blockShift, blockShift) * Math.sin(time * 5 + i)

    // 从原始画布获取块图像
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = blockWidth
    tempCanvas.height = blockHeight
    const tempCtx = tempCanvas.getContext('2d')!

    // 复制块区域
    tempCtx.drawImage(
      canvas,
      0, blockY, blockWidth, blockHeight,
      0, 0, blockWidth, blockHeight
    )

    // 绘制错位块
    ctx.drawImage(tempCanvas, shiftAmount, blockY)
  }
}

// ============================================
// 类封装（用于动画）
// ============================================

/**
 * Glitch 效果类
 */
export class GlitchEffect {
  private config: GlitchConfig
  private time: number = 0

  constructor(config: GlitchConfig) {
    this.config = config
  }

  /**
   * 更新时间
   */
  update(deltaTime: number): void {
    this.time += deltaTime
  }

  /**
   * 更新配置
   */
  updateConfig(config: GlitchConfig): void {
    this.config = config
  }

  /**
   * 应用效果
   */
  apply(canvas: HTMLCanvasElement): void {
    const config = { ...this.config, time: this.time }
    applyGlitch(canvas, config)
  }

  /**
   * 重置时间
   */
  reset(): void {
    this.time = 0
  }
}

// ============================================
// 配置工厂函数
// ============================================

/**
 * 从用户设置创建 Glitch 配置
 */
export function createGlitchConfigFromSettings(
  rgbShift: number,
  blockShift: number,
  blockCount: number,
  speed: number,
  direction: string
): GlitchConfig {
  return {
    rgbShift: clamp(Math.round(rgbShift), 0, 20),
    blockShift: clamp(Math.round(blockShift), 0, 50),
    blockCount: clamp(Math.round(blockCount), 0, 10),
    speed: clamp(speed, 0.1, 5),
    direction: direction as any,
    time: 0
  }
}

// ============================================
// 默认配置
// ============================================

/** 默认 Glitch 配置 - 参数已调整使效果更明显 */
export const DEFAULT_GLITCH_CONFIG: GlitchConfig = {
  rgbShift: 8,
  blockShift: 15,
  blockCount: 4,
  speed: 1.5,
  direction: 'right',
  time: 0
}